import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import TabNav from '../../composants/TabNav/TabNav';
import Button from '../../composants/Button/Button';
import ValidationPopUp from '../../composants/ValidationPopUp/ValidationPopUp';
import CompteARebourd from '../../composants/CompteARebourd/CompteARebourd';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import TableSkeleton from '../../composants/Skeleton/TableSkeleton';
import SearchBar from '../../composants/SearchBar/SearchBar';
import Banner from '../../composants/Banner/Banner';
import CreerCandidatureModal from './components/CreerCandidatureModal/CreerCandidatureModal';
import DemoCreerCandidatureModal from './components/CreerCandidatureModal/DemoCreerCandidatureModal';
import MesCandidaturesTab, {
  CandidatureResumee,
  InvitationGroupementResumee,
  ModalInvitationsGroupement,
} from './components/MesCandidaturesTab/MesCandidaturesTab';
import { useNotifications } from '../../contexts/NotificationsContextType';
import { useAuth } from '../../contexts/AuthContext';
import { isClientRole, isPrestataireRole } from '../../utils/roleUtils';
import { getParisLabel } from '../../utils/timezone';
import { LienExterne, LIEN_AAPC_JOUE, LIEN_AAPC_BOAMP, LIEN_PROFIL_ACHETEUR_AWS } from './liensConsultation';
import './Publications.css';

let FilterIcon: any = null;
try {
  FilterIcon = require('../../assets/icons/filter.svg').ReactComponent;
} catch (_) { }

// ── Types — alignés sur la réponse réelle de /publications/accord-cadre ──────

interface AccordCadreData {
  id: number;
  nom: string;
  annee: number;
  statut: 'brouillon' | 'configure' | 'ouvert' | 'ferme' | 'phase_2';
  nb_articles: number;
  acheteur?: string;
  reference_avis?: string;
  expire_at?: string;
  nombre_lots_techniques?: number;
  budget_estime?: number;
  source_financement?: string;
  type_marche_ccag?: string;
  categorie_cpv?: string;
  date_limite_candidature?: string;
  langue?: string;
  delai_validite_offre?: string;
  presentation_consultation?: string;
}

interface Publication {
  id: number;
  ref_avis: string;
  publie_le: string;
  acheteur: string;
  objet: string;
  categorie_cpv?: string;
  lieu_execution?: string;
  limite_depot: string;
  type: string;
  dossier_consultation?: string;
  questions_reponses: number;
  action: 'candidater' | 'reprendre' | 'reserve' | 'demo';
}

const DEMO_ROW: Publication = {
  id: -1,
  ref_avis: 'AC-2027-DÉMO',
  publie_le: '2027-01-10T00:00:00Z',
  acheteur: 'Démonstration Centralis',
  objet: 'Opération fictive',
  categorie_cpv: '54000000-7',
  lieu_execution: 'France',
  limite_depot: '2050-12-31T11:00:00Z',
  type: 'AAPC',
  questions_reponses: 5,
  action: 'demo',
};

// Onglets AAPC / Avis modificatifs / Avis d'attributions retires a la demande du
// metier : la consultation est publiee sur les AAPC (BOAMP, JOUE) et le profil
// acheteur AWS, plus dans un onglet Centralis. L'onglet "Tous" devient
// "Publications". Les definitions d'origine sont conservees en commentaire pour
// un retour arriere sans re-developpement.
type OngletFilter = 'Publications' | 'Mes candidatures';
// type OngletFilter = 'Tous' | 'AAPC' | 'Avis modificatifs' | "Avis d'attributions" | 'Mes candidatures';

const ONGLETS: OngletFilter[] = ['Publications', 'Mes candidatures'];
// const ONGLETS: OngletFilter[] = ['Tous', 'AAPC', 'Avis modificatifs', "Avis d'attributions", 'Mes candidatures'];

// Onglets 2 et 3 désactivés avec tooltip "Fonctionnalité en cours de développement"
const INACTIVE_TABS: number[] = [];
// const INACTIVE_TABS = [2, 3];

// ── Page principale ───────────────────────────────────────────────────────────

const Publications: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAcheteur = isClientRole(user?.role || '');
  // Candidature fictive : visible/accessible uniquement aux prestataires connectes (pas le public, pas les acheteurs)
  const isPrestataire = !!user && isPrestataireRole(user?.role || '');
  const { unreadCandidatures, setUnreadCandidatures } = useNotifications();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);

  // Ouvre l'onglet demandé via ?tab=4 (ex : redirect depuis /publications/ac27)
  const initialTab = searchParams.get('tab') === '4'
    ? ONGLETS.indexOf('Mes candidatures')
    : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [search, setSearch] = useState('');
  const handleSearch = (q: string) => setSearch(q);
  const [showCreerModal, setShowCreerModal] = useState(false);
  const [accordCadreId, setAccordCadreId] = useState<number | null>(null);
  const [accordCadreStatut, setAccordCadreStatut] = useState<string | null>(null);
  const [accordCadreFullData, setAccordCadreFullData] = useState<AccordCadreData | null>(null);
  // AC support de la candidature fictive : le plus récent (tous statuts), distinct de
  // accordCadreId qui vient de current() et exclut les brouillons.
  const [demoAccordCadreId, setDemoAccordCadreId] = useState<number | null>(null);

  // Story 9.0 — Onglet "Mes candidatures"
  const [candidatures, setCandidatures] = useState<CandidatureResumee[]>([]);
  const [invitations, setInvitations] = useState<InvitationGroupementResumee[]>([]);
  const [loadingCandidatures, setLoadingCandidatures] = useState(true);
  const [showInvitationsModal, setShowInvitationsModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showDemoUnavailable, setShowDemoUnavailable] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const handleDemoSimulation = async () => {
    // Page publique : candidater (fictive ou reelle) exige une connexion -> /login sinon.
    if (!user) { navigate('/login'); return; }

    // L'id de l'AC support de la simulation peut manquer au moment du clic : course avec le
    // chargement initial, ou echec transitoire du proxy AC (le fetch initial tombe en
    // .catch -> null). On le (re)recupere a la demande pour ne pas laisser le clic sans effet.
    let acId = demoAccordCadreId;
    if (!acId) {
      setDemoLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${process.env.REACT_APP_API_URL}/publications/ac27/demo-accord-cadre`, { headers });
        const d = await r.json();
        acId = d?.data?.id ?? null;
        if (acId) setDemoAccordCadreId(acId);
      } catch {
        acId = null;
      } finally {
        setDemoLoading(false);
      }
    }

    // Toujours donner un retour : sans AC support, on affiche un message au lieu d'un no-op muet.
    if (!acId) { setShowDemoUnavailable(true); return; }

    setShowDemoModal(true);
  };

  // Popup de fin de simulation démo (retour depuis DemoDetailCandidature)
  const [showDemoSuccess, setShowDemoSuccess] = useState(
    () => !!(location.state as { demoComplete?: boolean } | null)?.demoComplete
  );

  /** La candidature test (ref_avis contenant "test") est toujours en première position. */
  const sortCandidatures = (list: CandidatureResumee[]): CandidatureResumee[] =>
    [...list].sort((a, b) => {
      const aTest = /test/i.test(a.ref_avis ?? '');
      const bTest = /test/i.test(b.ref_avis ?? '');
      if (aTest !== bTest) return aTest ? -1 : 1;
      return 0;
    });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

    const fetchPublications = fetch(`${process.env.REACT_APP_API_URL}/publications`, { headers })
      .then((r) => r.json())
      .then((data) => {
        const raw = data?.data;
        return Array.isArray(raw) ? raw : [];
      })
      .catch(() => [] as Publication[]);

    const fetchAccordCadre = fetch(`${process.env.REACT_APP_API_URL}/publications/ac27`, { headers })
      .then((r) => r.json())
      .then((data) => data?.data ?? null)
      .catch(() => null);

    const fetchCandidatures = !isAcheteur && !!token
      ? fetch(`${process.env.REACT_APP_API_URL}/candidatures`, { headers })
        .then((r) => r.json())
        .then((data) => (Array.isArray(data?.data) ? data.data : []))
        .catch(() => [] as CandidatureResumee[])
      : Promise.resolve([] as CandidatureResumee[]);

    const fetchInvitations = !isAcheteur && !!token
      ? fetch(`${process.env.REACT_APP_API_URL}/publications/ac27/groupement-invitations`, { headers })
        .then((r) => r.json())
        .then((data) => (Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])))
        .catch(() => [] as InvitationGroupementResumee[])
      : Promise.resolve([] as InvitationGroupementResumee[]);

    // AC le plus récent (tous statuts) pour la candidature fictive — prestataires connectes seulement
    const fetchDemoAccordCadre = isPrestataire
      ? fetch(`${process.env.REACT_APP_API_URL}/publications/ac27/demo-accord-cadre`, { headers })
        .then((r) => r.json())
        .then((data) => data?.data ?? null)
        .catch(() => null)
      : Promise.resolve(null);

    Promise.all([fetchPublications, fetchAccordCadre, fetchCandidatures, fetchInvitations, fetchDemoAccordCadre])
      .then(([pubs, ac, cands, invs, demoAc]) => {
        setPublications(pubs);
        if (ac?.id) setAccordCadreId(ac.id);
        if (ac?.statut) setAccordCadreStatut(ac.statut);
        if (ac) setAccordCadreFullData(ac);
        if (demoAc?.id) setDemoAccordCadreId(demoAc.id);
        setCandidatures(sortCandidatures(cands));
        setInvitations(invs);
        if (!isAcheteur) {
          setUnreadCandidatures(cands.filter((c: any) => c.statut === 'brouillon').length);
          const pending = invs.filter((i: InvitationGroupementResumee) => i.status === 'pending');
          if (pending.length > 0) setShowInvitationsModal(true);
        }
      })
      .finally(() => {
        setLoading(false);
        setLoadingCandidatures(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Onglets visibles selon le rôle : les acheteurs ne voient pas "Mes candidatures"
  const visibleOnglets = isAcheteur
    ? ONGLETS.filter(o => o !== 'Mes candidatures')
    : ONGLETS;

  const filtered = useMemo(() => {
    let list = publications;

    // Filtre onglet — les filtres par type d'avis suivent les onglets retires (voir ONGLETS).
    const onglet = visibleOnglets[activeTab];
    // if (onglet === 'AAPC') list = list.filter((p) => p.type === 'AAPC');
    // else if (onglet === 'Avis modificatifs') list = list.filter((p) => p.type === 'Avis modificatif');
    // else if (onglet === "Avis d'attributions") list = list.filter((p) => p.type === "Avis d'attribution");

    // Filtre recherche (ref, objet, acheteur)
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.ref_avis.toLowerCase().includes(q) ||
          p.objet.toLowerCase().includes(q) ||
          p.acheteur.toLowerCase().includes(q),
      );
    }

    // Tri par date de publication descendant (les plus récentes en premier)
    list = [...list].sort((a, b) => {
      const da = a.publie_le ? new Date(a.publie_le).getTime() : 0;
      const db = b.publie_le ? new Date(b.publie_le).getTime() : 0;
      return db - da;
    });

    // Ligne de demonstration visible par tous sur l'onglet "Tous" (public, acheteur, prestataire).
    // Le droit de cliquer est gere au niveau des boutons (desactives pour l'acheteur, -> /login si non connecte).
    if (onglet === 'Publications') {
      list = [DEMO_ROW, ...list];
    }

    return list;
  }, [publications, activeTab, search, visibleOnglets, isAcheteur, isPrestataire]);

  const columns = useMemo(() => [
    {
      field: 'ref_avis',
      header: 'Ref avis',
      renderCell: (value: string) => (
        <div className="text-truncate" title={value}>{value}</div>
      ),
    },
    {
      // Supports de publication de l'avis d'appel public a la concurrence.
      // Champ absent des lignes renvoyees par l'API : la cellule se construit
      // depuis la ligne, pas depuis une valeur.
      // Champ absent des lignes renvoyees par l'API : la cellule se construit
      // depuis la ligne, pas depuis une valeur — donc rien a trier.
      field: 'aapc',
      header: 'AAPC',
      renderCell: (_value: unknown, row: Publication) => (
        // La ligne de demonstration ne renvoie vers aucun avis reel.
        row.action === 'demo'
          ? <div>—</div>
          : (
            <div className="publications-aapc-cell">
              <LienExterne href={LIEN_AAPC_JOUE} label="JOUE" />
              <LienExterne href={LIEN_AAPC_BOAMP} label="BOAMP" />
            </div>
          )
      ),
    },
    {
      field: 'publie_le',
      header: 'Publié le',
      renderCell: (value: string) => {
        const d = value ? new Date(value) : null;
        return <div>{d && !isNaN(d.getTime()) && d.getFullYear() < 9000 ? d.toLocaleDateString('fr-FR') : '—'}</div>;
      },
    },
    {
      field: 'acheteur',
      header: 'Acheteur',
      renderCell: (value: string) => (
        <div className="text-truncate" title={value}>{value}</div>
      ),
    },
    {
      field: 'objet',
      header: 'Objet',
      renderCell: (value: string) => (
        <div className="text-truncate" title={value}>{value}</div>
      ),
    },
    {
      field: 'categorie_cpv',
      header: 'Cat. CPV',
      renderCell: (value: string) => <div>{value || '—'}</div>,
    },
    {
      field: 'lieu_execution',
      header: "Lieu d'exécution",
      renderCell: (value: string) => (
        <div className="text-truncate" title={value}>{value}</div>
      ),
    },
    {
      field: 'limite_depot',
      header: 'Limite de dépôt',
      renderCell: (value: string) => {
        const d = value ? new Date(value) : null;
        if (d && d.getFullYear() >= 9000) return <div style={{ fontSize: '1.2em' }}>—</div>;
        return (
          <div>
            {d && !isNaN(d.getTime()) ? d.toLocaleString('fr-FR') : '—'}
            {d && !isNaN(d.getTime()) && (
              <span style={{ display: 'block', fontSize: '0.75em', color: '#6b7280' }}>{getParisLabel()}</span>
            )}
          </div>
        );
      },
    },
    {
      field: 'limite_depot',
      header: 'Temps restant',
      renderCell: (value: string) => {
        const d = value ? new Date(value) : null;
        if (d && d.getFullYear() >= 9000) return <div style={{ fontSize: '1.2em' }}>—</div>;
        return <CompteARebourd targetDate={value} autoReload={false} />;
      },
    },
    {
      // Profil acheteur utilise par Centralis pour la consultation en cours.
      // Le type d'avis renvoye par l'API n'est plus affiche tel quel.
      // Cle de tri volontairement absente des lignes : la cellule affiche un
      // libelle constant, un tri sur `type_avis` (qui n'est plus visible)
      // reordonnerait le tableau sans cause lisible a l'ecran.
      field: 'profil_acheteur',
      header: 'Profil Acheteur',
      renderCell: (_value: string, row: Publication) => (
        row.action === 'demo'
          ? <div>AWS</div>
          : <div><LienExterne href={LIEN_PROFIL_ACHETEUR_AWS} label="AWS" /></div>
      ),
    },
    {
      // Les questions de la consultation se posent sur le profil acheteur AWS,
      // plus dans Centralis : le compteur d'echanges laisse place au renvoi AWS.
      // Meme raison que 'Profil Acheteur' : le compteur d'echanges n'est plus
      // affiche, un tri dessus serait invisible donc incomprehensible.
      field: 'questions_reponses_lien',
      header: 'Questions/réponses',
      renderCell: (_value: number, row: Publication) => (
        row.action === 'demo'
          ? <div>AWS</div>
          : <div><LienExterne href={LIEN_PROFIL_ACHETEUR_AWS} label="AWS" /></div>
      ),
    },
    {
      field: 'action',
      header: 'Action',
      renderCell: (value: Publication['action'], row: Publication) => {
        if (value === 'demo') {
          return (
            <div className="publication-action-buttons">
              <Button
                type="button"
                variant="secondary"
                value={demoLoading
                  ? <span className="demo-btn-spinner" role="status" aria-label="Chargement…" />
                  : 'Candidature fictive'}
                size="small"
                disabled={isAcheteur || demoLoading}
                onClick={(e?: React.MouseEvent) => { e?.stopPropagation(); handleDemoSimulation(); }}
              />
            </div>
          );
        }
        if (value === 'reserve') {
          return (
            <div className="publication-action-buttons">
              <Button
                type="button"
                variant="secondary"
                value="Réservé aux titulaires"
                size="small"
                disabled
              />
            </div>
          );
        }
        if (value === 'reprendre') {
          if (isAcheteur) return null;
          return (
            <div className="publication-action-buttons">
              <Button
                type="button"
                variant="primary"
                value="Reprendre"
                size="small"
                onClick={() => setActiveTab(visibleOnglets.indexOf('Mes candidatures'))}
              />
            </div>
          );
        }
        return (
          <div className="publication-action-buttons">
            <Button
              type="button"
              variant="primary"
              value={accordCadreStatut === 'phase_2' ? 'Candidater (Phase 2)' : 'Candidater'}
              size="small"
              onClick={() => user ? setShowCreerModal(true) : navigate('/login')}
              disabled={isAcheteur || !accordCadreId || accordCadreStatut === 'ferme'}
            />
            {candidatures.length > 0 && (
              <Button
                type="button"
                variant="secondary"
                value="Voir mes candidatures"
                size="small"
                onClick={() => setActiveTab(visibleOnglets.indexOf('Mes candidatures'))}
              />
            )}
          </div>
        );
      },
    },
  ], [navigate, accordCadreId, setActiveTab, isAcheteur, visibleOnglets, candidatures, loadingCandidatures, user, setShowCreerModal, demoLoading, demoAccordCadreId]);

  const mesCandidaturesIdx = visibleOnglets.indexOf('Mes candidatures');
  const isMesCandidaturesTab = !isAcheteur && activeTab === mesCandidaturesIdx;

  const dynamicInactiveTabs = [
    ...INACTIVE_TABS,
    ...(!user && mesCandidaturesIdx >= 0 ? [mesCandidaturesIdx] : []),
  ];
  const dynamicInactiveTooltips: Record<string, string> = !user && mesCandidaturesIdx >= 0
    ? { 'Mes candidatures': 'Connectez-vous pour accéder à vos candidatures' }
    : {};

  const refreshCandidatures = () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoadingCandidatures(true);
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/candidatures`, { headers })
        .then((r) => r.json())
        .then((data) => (Array.isArray(data?.data) ? data.data : [])),
      fetch(`${process.env.REACT_APP_API_URL}/publications/ac27/groupement-invitations`, { headers })
        .then((r) => r.json())
        .then((data) => (Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []))),
    ])
      .then(([cands, invs]) => {
        setCandidatures(sortCandidatures(cands));
        setInvitations(invs);
        setUnreadCandidatures(cands.filter((c: any) => c.statut === 'brouillon').length);
        const stillPending = invs.filter((i: InvitationGroupementResumee) => i.status === 'pending');
        if (stillPending.length === 0) setShowInvitationsModal(false);
      })
      .catch(() => {
        setCandidatures([]);
        setInvitations([]);
      })
      .finally(() => setLoadingCandidatures(false));
  };

  return (
    <div className="publications-container">
      {/* Onglets — 2 et 3 désactivés */}
      <TabNav
        tabs={visibleOnglets}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        inactiveTabs={dynamicInactiveTabs}
        inactiveTabsTooltips={dynamicInactiveTooltips}
        notifications={
          !isAcheteur && unreadCandidatures > 0
            ? [{ tabIndex: visibleOnglets.indexOf('Mes candidatures'), count: unreadCandidatures }]
            : []
        }
      />

      {/* Onglet "Mes candidatures" */}
      {isMesCandidaturesTab ? (
        <MesCandidaturesTab
          candidatures={candidatures}
          invitations={invitations}
          loading={loadingCandidatures}
          onRefresh={refreshCandidatures}
          accordCadreName={accordCadreFullData?.nom}
          onDeleteCandidature={(id) => {
            const token = localStorage.getItem('token');
            if (!window.confirm('Voulez-vous vraiment supprimer cette candidature ?')) return;
            fetch(`${process.env.REACT_APP_API_URL}/publications/ac27/candidatures/${id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token}` },
            })
              .then((r) => {
                if (r.ok) refreshCandidatures();
                else alert('Erreur lors de la suppression.');
              })
              .catch(() => alert('Erreur réseau.'));
          }}
        />
      ) : (
        <>
          {/* Barre de recherche + filtre */}
          <div className="button-group">
            <div className="button-filter-group">
              <SearchBar
                onSearch={handleSearch}
                onClear={() => handleSearch('')}
                placeholder="Trouver une publication"
              />
              <Button
                type="button"
                variant="secondary"
                value="filtrer"
                size="small"
                icon={FilterIcon ? <FilterIcon /> : null}
              />
            </div>
          </div>

          {/* Toujours affichee : rappel du parcours candidature. Les mots-cles sont des liens
              de redirection pour un visiteur, du simple texte (non cliquable) une fois connecte. */}
          <Banner
            variant="info"
            className="publications-auth-banner"
            message={(
              <>
                <span className="publications-banner-paragraphe">
                  {"Nous vous invitons à vous rendre sur les AAPC (BOAMP et JOUE) puis sur le profil acheteur AWS utilisé par Centralis (voir le lien dans le tableau ci-dessous)."}
                </span>
                {"Pour candidater avec le catalogue électronique mis à disposition, il convient de "}
                {user
                  ? <span className="publications-banner-strong">{"s'inscrire"}</span>
                  : <span className="publications-banner-link" onClick={() => navigate('/register')}>{"s'inscrire"}</span>}
                {" sur Centralis, sauf si votre organisme (SIRET) dispose déjà d'un compte. Dans ce cas, "}
                {user
                  ? <span className="publications-banner-strong">{"connectez-vous"}</span>
                  : <span className="publications-banner-link" onClick={() => navigate('/login')}>{"connectez-vous"}</span>}
                {" avec ce compte. Si vous envisagez un groupement, chaque membre doit s'inscrire séparément préalablement."}
              </>
            )}
          />

          {/* Table via TableauDeBord — skeleton du tableau pendant le chargement (par onglet). */}
          {(loading || (!isAcheteur && loadingCandidatures)) ? (
            <TableSkeleton rows={8} columns={columns} />
          ) : (
            <TableauDeBord
              data={filtered}
              columns={columns}
              showActionsColumn={false}
              isSelectable={false}
              showActionBar={false}
              selectedItems={[]}
              onSelectionChange={() => { }}
              onRowClick={(id) => { if (String(id) === '-1') return; navigate(`/publications/ac27/${id}/informations`); }}
              getRowStyle={(row) => row.action === 'demo'
                ? { backgroundColor: '#e5e7eb', color: '#374151', cursor: 'default' }
                : {}}
            />
          )}
        </>
      )}

      {/* Modal invitations groupement */}
      {showInvitationsModal && invitations.filter(i => i.status === 'pending').length > 0 && (
        <ModalInvitationsGroupement
          invitations={invitations.filter(i => i.status === 'pending')}
          accordCadreName={accordCadreFullData?.nom}
          onClose={() => setShowInvitationsModal(false)}
          onHandled={() => {
            refreshCandidatures();
          }}
        />
      )}

      {/* Modal démo candidature */}
      {/* Popup fin de simulation démo */}
      <ValidationPopUp
        isOpen={showDemoSuccess}
        title="Simulation terminée"
        text="Votre simulation de candidature et de dépôt de l'offre est terminée. Aucune donnée n'a été enregistrée pour cette simulation. Vous pouvez maintenant déposer une vraie offre."
        confirmLabel="Compris"
        onClose={() => setShowDemoSuccess(false)}
        onConfirm={() => setShowDemoSuccess(false)}
      />

      {showDemoModal && demoAccordCadreId && (
        <DemoCreerCandidatureModal
          accordCadreId={demoAccordCadreId}
          onClose={() => setShowDemoModal(false)}
        />
      )}

      <ValidationPopUp
        isOpen={showDemoUnavailable}
        title="Simulation indisponible"
        text="Aucun accord-cadre n'est disponible pour la simulation pour le moment. Réessayez dans un instant."
        confirmLabel="Compris"
        onClose={() => setShowDemoUnavailable(false)}
        onConfirm={() => setShowDemoUnavailable(false)}
      />

      {/* Modal création candidature */}
      {showCreerModal && accordCadreId && (
        <CreerCandidatureModal
          accordCadreId={accordCadreId}
          accordCadreStatut={accordCadreStatut ?? undefined}
          onClose={() => setShowCreerModal(false)}
          onSuccess={(id) => {
            setShowCreerModal(false);
            navigate(`/publications/ac27/candidatures/${id}`);
          }}
        />
      )}
    </div>
  );
};

export default Publications;
